<template>
    <div class="container offline-page">
        <div class="common-header-wrap">
            <mt-header class="common-header" title="Bank Transfer Payment">
                <mt-button icon="back" slot="left" @click="goBack"></mt-button>
            </mt-header>
        </div>
        <div v-if="loading" class="page-state">Loading...</div>
        <div v-else-if="loadError" class="page-state page-state-error">{{loadError}}</div>
        <main v-else-if="isReady" ref="imageTofile" class="offline-content">
          <section class="page-card hero-card">
            <div class="hero-heading">
              <div>
                <div class="section-kicker">BANK TRANSFER</div>
                <h1>Wire Transfer Account</h1>
                <div class="order-reference">{{referenceLabel}}: <strong>{{referenceNumber}}</strong></div>
              </div>
            </div>
            <div class="hero-status" :class="'status-' + paymentStatusClass">{{paymentStatusText}}</div>
          </section>

          <section class="page-card summary-card">
            <div class="section-title">Order Summary</div>
            <div class="summary-grid">
              <div class="summary-row"><span>Buyer</span><strong>{{pay_info.member_nickname || '-'}}</strong></div>
              <div class="summary-row" v-if="incotermText"><span>Incoterms</span><strong>{{incotermText}}</strong></div>
              <div class="summary-row" v-if="shippingFee !== null"><span>Shipping Fee</span><strong>{{formatCurrency(shippingFee)}}</strong></div>
              <div class="summary-row"><span>Estimated volume</span><strong>{{formatNumber(order_info.goods_volume_total)}} m³</strong></div>
              <div class="summary-row"><span>Gross weight</span><strong>{{formatNumber(order_info.goods_weight_total)}} kg</strong></div>
              <div class="summary-row total-row"><span>Payment Total</span><b>{{formatCurrency(order_info.order_amount)}}</b></div>
            </div>
            <p class="reference-note">Volume and weight are for reference only. Actual production confirmation shall prevail.</p>
          </section>

          <section class="page-card progress-card" v-if="paymentPhases.length">
            <div class="section-title">Payment Progress</div>
            <div class="progress-summary">
              <span>{{paymentStatusText}}</span>
              <strong>{{formatCurrency(confirmedAmount)}} / {{formatCurrency(order_info.order_amount)}}</strong>
            </div>
            <div class="progress-list">
              <div class="progress-item" v-for="phase in paymentPhases" :key="phase.key" :class="'phase-' + phaseStatus(phase.key)">
                <div class="progress-marker">
                  <span v-if="phaseStatus(phase.key) === 'complete'" class="progress-check" aria-hidden="true"></span>
                  <span v-else>{{phase.key}}</span>
                </div>
                <div class="progress-line"></div>
                <div class="progress-body">
                  <div class="progress-label">{{phase.label}} <span v-if="phase.ratio">{{phase.ratio}}%</span></div>
                  <div class="progress-amount">{{formatCurrency(phase.amount)}}</div>
                  <div class="progress-time" v-if="phase.approved_time">{{phase.approved_time}}</div>
                </div>
              </div>
            </div>
          </section>

          <section class="page-card interim-card" v-if="canSelectInterimRatio">
            <div class="section-title">Select Interim Payment Ratio</div>
            <p class="interim-desc">The deposit has been approved. Please select the interim payment ratio:</p>
            <div class="interim-options">
              <button
                v-for="item in interimRatioOptions"
                :key="item.ratio"
                type="button"
                class="interim-option"
                :class="{active: selectedInterimRatio === item.ratio, disabled: item.disabled}"
                :disabled="item.disabled || interimSubmitting"
                @click="selectInterimRatio(item.ratio)"
              >
                <span></span>{{item.ratio}}%
              </button>
            </div>
            <div class="interim-amount">Interim Payment Amount: {{formatCurrency(interimPaymentAmount)}}</div>
            <button class="interim-confirm no-capture" type="button" :disabled="!selectedInterimRatio || interimSubmitting" @click="confirmInterimRatio">Confirm</button>
          </section>

          <section class="page-card product-card">
            <div class="section-title">Product List <span>{{totalProductCount}} item(s)</span></div>
            <div class="store-block" v-for="store in storeList" :key="store.store_id || store.store_name">
              <div class="store-heading"><span class="iconfont">&#xe62d;</span>{{store.store_name || 'Store'}}</div>
              <div class="product-item" v-for="item in store.order_goods" :key="item.goods_id + '-' + item.goods_num" @click="goGoods(item.goods_id)">
                <img class="product-image" :src="getImageUrl(item)" alt="">
                <div class="product-main">
                  <div class="product-name">{{item.goods_name}}</div>
                  <div class="product-meta">{{formatCurrency(item.goods_price)}} × {{item.goods_num}}</div>
                  <div class="product-meta" v-if="item.goods_volume_total || item.goods_weight_total">{{formatNumber(item.goods_volume_total)}} m³ · {{formatNumber(item.goods_weight_total)}} kg</div>
                </div>
                <div class="product-subtotal">{{formatCurrency(item.goods_pay_price || item.goods_price * item.goods_num)}}</div>
              </div>
            </div>
          </section>

          <section class="page-card container-card" v-if="containerRecommend && containerRecommend.containers && containerRecommend.containers.length">
            <div class="section-title">Container Recommendation</div>
            <div class="container-summary">
              <div><span>Total Items</span><strong>{{containerRecommend.total_items}}</strong></div>
              <div><span>Total Volume</span><strong>{{formatNumber(containerRecommend.total_volume)}} m³</strong></div>
            </div>
            <div class="container-type-tabs">
              <button
                v-for="container in containerOptions"
                :key="container.type"
                class="type-tab"
                :class="{active: activeContainer && activeContainer.type === container.type}"
                type="button"
                @click="selectContainer(container.type)"
              >{{formatContainerType(container.type)}}</button>
            </div>
            <div class="container-list" v-if="activeContainer">
              <div class="container-item">
                <div class="container-heading">
                  <div>
                    <strong>{{activeContainer.type}}</strong>
                    <span v-if="activeContainer.needed_count > 1">× {{activeContainer.needed_count}}</span>
                  </div>
                  <em>{{activeContainer.name}}</em>
                </div>
                <div class="container-visual">
                  <div class="container-svg-wrapper">
                    <svg viewBox="0 0 320 80" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <g transform="translate(0, 10)">
                        <path d="M5,45 L5,25 Q5,15 15,12 L35,8 Q45,5 50,5 L55,5 L55,50 L5,50 Z" fill="#c0392b"/>
                        <rect x="38" y="12" width="14" height="10" rx="2" fill="#2c3e50"/>
                        <rect x="22" y="14" width="12" height="8" rx="1" fill="#2c3e50"/>
                        <rect x="28" y="24" width="18" height="20" rx="1" fill="#a93226"/>
                        <circle cx="8" cy="42" r="3" fill="#f1c40f"/>
                        <circle cx="8" cy="48" r="2" fill="#ecf0f1"/>
                        <rect x="2" y="48" width="8" height="6" rx="2" fill="#7f8c8d"/>
                        <circle cx="28" cy="55" r="10" fill="#2c3e50"/>
                        <circle cx="28" cy="55" r="5" fill="#ecf0f1"/>
                        <circle cx="28" cy="55" r="2" fill="#2c3e50"/>
                      </g>
                      <rect x="55" y="52" width="8" height="6" fill="#7f8c8d"/>
                      <g transform="translate(60, 8)">
                        <rect x="0" y="0" width="255" height="50" rx="2" fill="#c0392b" stroke="#922b21" stroke-width="2"/>
                        <line v-for="x in containerRibs" :key="x" :x1="x" y1="2" :x2="x" y2="48" stroke="#922b21" stroke-width="2"/>
                        <rect x="0" y="50" width="255" height="8" fill="#5d6d7e"/>
                      </g>
                      <circle v-for="wheel in containerWheels" :key="wheel" :cx="wheel" cy="65" r="10" fill="#2c3e50"/>
                      <circle v-for="wheel in containerWheels" :key="'inner-' + wheel" :cx="wheel" cy="65" r="5" fill="#ecf0f1"/>
                      <circle v-for="wheel in containerWheels" :key="'dot-' + wheel" :cx="wheel" cy="65" r="2" fill="#2c3e50"/>
                    </svg>
                    <div class="container-fill-bar" :class="{high: containerRatio(activeContainer) > 75}" :style="containerFillStyle(activeContainer)">
                      <span v-if="containerRatio(activeContainer) >= 15" class="container-fill-text">{{containerRatio(activeContainer)}}%</span>
                    </div>
                    <span v-if="containerRatio(activeContainer) < 15" class="container-fill-text-outside" :style="containerFillOutsideStyle(activeContainer)">{{containerRatio(activeContainer)}}%</span>
                  </div>
                  <div class="container-label">Volume</div>
                </div>
                <div class="container-info">
                  <div class="container-info-row"><span>Container Volume:</span><strong>{{formatNumber(activeContainer.volume)}} m³</strong></div>
                  <div class="container-info-row"><span>Max Load</span><strong>{{formatNumber(activeContainer.max_weight)}} kg</strong></div>
                  <div class="container-info-row"><span>Volume Used</span><strong :class="containerUsageClass(activeContainer)">{{containerRatio(activeContainer)}}%</strong></div>
                </div>
              </div>
            </div>
            <p class="reference-note">Container loading efficiency may vary based on actual packaging and arrangement.</p>
          </section>

          <section class="page-card bank-card">
            <div class="section-title">Bank Account Information</div>
            <div class="bank-row" v-for="field in bankFields" :key="field.label" v-if="field.value">
              <span>{{field.label}}</span>
              <div class="bank-value"><strong>{{field.value}}</strong></div>
            </div>
          </section>

          <section class="page-card important-card">
            <div class="section-title">Important Notice</div>
            <p>1. Please make sure to include your {{referenceLabel}} in the payment reference.</p>
            <p>2. Please upload your payment receipt below after transfer.</p>
            <p>3. You will ONLY be covered by Trade Assurance when you make payment to the account shown above.</p>
            <div class="print-date" v-if="pay_info.print_date">Print date: {{pay_info.print_date}}</div>
          </section>

          <section class="page-card upload-card" v-if="!isCompleted && !canSelectInterimRatio">
            <div class="section-title">Upload Payment Voucher</div>
            <div class="current-payment">
              <span>{{currentPhaseLabel}}</span>
              <strong>{{formatCurrency(currentPaymentAmount)}}</strong>
            </div>
            <div class="upload-pending" v-if="isPaymentPending">{{currentPhaseLabel}} voucher has been uploaded and is pending review.</div>
            <div class="upload-pending upload-rejected" v-if="isPaymentRejected">The previous voucher was rejected. Please upload a new one.</div>
            <a class="voucher-link" v-if="currentVoucherUrl" :href="currentVoucherUrl" target="_blank">View uploaded voucher</a>
            <a class="voucher-preview" v-if="currentVoucherUrl && !canUpload" :href="currentVoucherUrl" target="_blank">
              <img :src="currentVoucherUrl" alt="Payment voucher">
            </a>
            <div class="offline_info_img" v-if="canUpload" @change="uploadImage($event)">
              <div v-if="currentVoucherUrl" class="user-avatar"><img :src="currentVoucherUrl" alt="Payment voucher"></div>
              <div v-else class="upload-placeholder"><span class="iconfont">&#xe6f2;</span><span>Tap to upload receipt</span></div>
              <input type="file" accept="image/*">
            </div>
            <button v-if="canUpload" class="submit-button no-capture" type="button" @click="submit">Submit Payment</button>
          </section>
          <section class="page-card completed-card" v-else>
            <span class="completed-check" aria-hidden="true"></span>
            <strong>Payment completed</strong>
          </section>
        </main>
        <div v-else class="page-state">No payment information found.</div>

        <div v-if="isReady" class="export-bar no-capture">
          <button class="export-action export-pdf" type="button" @click="exportFile('pdf')">Export PDF</button>
          <button class="export-action export-excel" type="button" @click="exportFile('excel')">Export Excel</button>
        </div>

        <mt-popup position="right" v-model="showCrop" class="crop-wrapper common-popup-wrapper">
          <div class="common-header-wrap">
            <mt-header title="Crop the picture" class="common-header">
              <mt-button slot="left" icon="back" @click="showCrop=false"></mt-button>
              <mt-button slot="right" @click="useCrop">Use</mt-button>
            </mt-header>
          </div>
          <vue-cropper class="common-popup-content" style="height: auto;position: absolute" ref="cropper" :maxImgSize="cropperOption.maxImgSize" :autoCropWidth="cropperOption.autoCropWidth" :autoCropHeight="cropperOption.autoCropHeight" :autoCrop="cropperOption.autoCrop" :img="cropperOption.img" :canMove="cropperOption.canMove" :outputType="cropperOption.outputType"></vue-cropper>
        </mt-popup>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { Toast, MessageBox, Indicator } from 'mint-ui'
import axios from 'axios'

import html2canvas from 'html2canvas'
import { getOrderpayOfflineInfo, selectInstallmentRatio, uploadPaymentPic, updatePaymentInfo } from '../../../api/memberBuy'
import { VueCropper } from 'vue-cropper'

export default {
  components: {
    VueCropper
  },
  name: 'pay',
  data () {
    return {
      pay_sn: this.$route.query.pay_sn ? this.$route.query.pay_sn : '',
      // 订单支付显示的数据
      pay_info: [],
      order_info: [],
      store_cart_list: [],
      container_recommend: null,
      activeContainerType: '',
      defaultContainerTypes: [
        { type: '20OT', name: "20' Open Top", volume: 32.5, max_weight: 21740 },
        { type: '20GP', name: "20' Standard", volume: 33.2, max_weight: 21740 },
        { type: '40OT', name: "40' Open Top", volume: 66.5, max_weight: 26740 },
        { type: '40GP', name: "40' Standard", volume: 67.7, max_weight: 26740 },
        { type: '40HQ', name: "40' High Cube", volume: 76.3, max_weight: 26110 },
        { type: '45HQ', name: "45' High Cube", volume: 86, max_weight: 27620 }
      ],
      containerRibs: [15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180, 195, 210, 225, 240],
      containerWheels: [105, 135, 220, 250],
      showNotice: 0,
      notice: this.$route.query.notice,
      loading: true,
      loadError: '',
      selectedInterimRatio: 0,
      interimSubmitting: false,
      showCrop: false,
      cropType: 'initial_img',
      cropperOption: {
        img: '',
        canMove: false,
        autoCrop: true,
        autoCropWidth: 100,
        autoCropHeight: 100,
        maxImgSize: 500,
        outputType: 'png'
      }
    }
  },
  computed: {
    ...mapState({
      token: state => state.member.token
    }),
    isReady () {
      return !this.loading && !this.loadError && !!(this.pay_info && this.order_info && Object.keys(this.pay_info).length && Object.keys(this.order_info).length)
    },
    storeList () {
      var stores = this.store_cart_list || []
      if (Array.isArray(stores)) {
        return stores
      }
      return Object.keys(stores).map(key => {
        var store = stores[key] || {}
        if (!store.store_id) store.store_id = key
        return store
      })
    },
    totalProductCount () {
      return this.storeList.reduce((total, store) => {
        return total + ((store.order_goods || []).length)
      }, 0)
    },
    referenceLabel () {
      return this.pay_info.pi_no || this.order_info.pi_no ? 'PI NO.' : 'Order Number'
    },
    referenceNumber () {
      return this.pay_info.pi_no || this.order_info.pi_no || this.pay_info.pay_sn || this.pay_sn
    },
    incotermText () {
      var incoterm = this.order_info.incoterm || this.order_info.incoterm_code
      if (!incoterm) return ''
      if (typeof incoterm === 'string') {
        return incoterm + (this.order_info.incoterm_place ? ' ' + this.order_info.incoterm_place : '')
      }
      return (incoterm.code || '') + (incoterm.place ? ' ' + incoterm.place : '')
    },
    shippingFee () {
      if (this.order_info.shipping_fee === undefined || this.order_info.shipping_fee === null) return null
      return parseFloat(this.order_info.shipping_fee) > 0 ? this.formatNumber(this.order_info.shipping_fee) : null
    },
    currentPhase () {
      return parseInt(this.order_info.payment_phase || this.pay_info.payment_phase || 1, 10)
    },
    initialRatio () {
      return parseInt(this.order_info.initial_ratio || this.pay_info.initial_ratio || 100, 10)
    },
    maxInterimRatio () {
      return Math.max(0, 100 - this.initialRatio)
    },
    canSelectInterimRatio () {
      return this.currentPhase === 1 && parseInt(this.pay_info.initial_state, 10) === 2 && !this.isCompleted && this.maxInterimRatio >= 10
    },
    interimRatioOptions () {
      var options = []
      for (var ratio = 10; ratio <= 90; ratio += 10) {
        options.push({
          ratio: ratio,
          disabled: ratio > this.maxInterimRatio
        })
      }
      return options
    },
    interimPaymentAmount () {
      var total = parseFloat(this.order_info.order_amount) || 0
      return Math.round(total * (parseInt(this.selectedInterimRatio, 10) || 0)) / 100
    },
    paymentPhases () {
      var phases = this.order_info.phases
      if (phases && typeof phases === 'object') {
        var keys = Object.keys(phases).sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
        if (keys.length) {
          return keys.map(key => {
            var phase = phases[key] || {}
            return {
              key: parseInt(key, 10),
              ratio: phase.ratio,
              amount: phase.amount,
              approved_time: phase.approved_time || phase.approvedTime,
              img_url: phase.img_url || phase.image_url || phase.voucher_url || '',
              state: phase.state,
              label: this.phaseLabel(parseInt(key, 10), keys.length)
            }
          })
        }
      }
      var total = parseFloat(this.order_info.order_amount) || 0
      var ratio = parseInt(this.pay_info.initial_ratio, 10) || 100
      if (ratio >= 100) {
        return [{ key: 1, ratio: 100, amount: total, label: 'Payment' }]
      }
      return [
        { key: 1, ratio: ratio, amount: this.order_info.initial_amount, img_url: this.pay_info.initial_img_url || '', label: 'Deposit' },
        { key: 2, ratio: 100 - ratio, amount: this.order_info.balance_amount, img_url: this.pay_info.balance_img_url || '', label: 'Balance Payment' }
      ]
    },
    confirmedAmount () {
      if (this.order_info.confirmed_amount !== undefined && this.order_info.confirmed_amount !== null) {
        return this.order_info.confirmed_amount
      }
      var state = parseInt(this.pay_info.initial_state, 10)
      if (this.isCompleted) return this.order_info.order_amount
      if (this.currentPhase > 1 || state === 2) return this.order_info.initial_amount || 0
      return 0
    },
    currentPhaseData () {
      return this.paymentPhases[this.currentPhase - 1] || this.paymentPhases[0] || {}
    },
    currentPhaseLabel () {
      return this.currentPhaseData.label || 'Payment'
    },
    currentPaymentAmount () {
      return this.currentPhaseData.amount || 0
    },
    currentPaymentState () {
      const phaseState = this.currentPhaseData.state
      if (phaseState !== undefined && phaseState !== null && phaseState !== '') {
        return parseInt(phaseState, 10)
      }
      if (this.currentPhase > 1) {
        const balanceState = this.pay_info.balance_state
        return balanceState === undefined || balanceState === null || balanceState === ''
          ? 0
          : parseInt(balanceState, 10)
      }
      return parseInt(this.pay_info.initial_state, 10)
    },
    currentImageField () {
      return this.currentPhase > 1 ? 'balance_img' : 'initial_img'
    },
    currentVoucherUrl () {
      if (this.currentPhaseData.img_url) return this.currentPhaseData.img_url
      return this.pay_info[this.currentImageField + '_url'] || ''
    },
    isCompleted () {
      return this.order_info.is_completed === true || this.order_info.is_completed === 1 || this.currentPaymentState === 4 || (parseInt(this.pay_info.initial_ratio, 10) >= 100 && parseInt(this.pay_info.initial_state, 10) === 2)
    },
    isPaymentPending () {
      return this.currentPaymentState === 1
    },
    isPaymentRejected () {
      return [10, 30, 50].indexOf(this.currentPaymentState) !== -1
    },
    canUpload () {
      return !this.isCompleted && !this.isPaymentPending && [0, 10, 30, 50].indexOf(this.currentPaymentState) !== -1
    },
    paymentStatusClass () {
      if (this.isCompleted) return 'complete'
      if (this.isPaymentPending) return 'pending'
      if (this.isPaymentRejected) return 'rejected'
      if (this.currentPaymentState === 2) return 'approved'
      return 'action'
    },
    paymentStatusText () {
      if (this.order_info.order_state_text || this.pay_info.order_state_text) return this.order_info.order_state_text || this.pay_info.order_state_text
      if (this.isCompleted) return 'Payment completed'
      if (this.isPaymentPending) return 'Payment under review'
      if (this.isPaymentRejected) return 'Payment voucher rejected'
      if (this.currentPaymentState === 2) return 'Payment approved'
      return 'Waiting for payment'
    },
    bankInfo () {
      var raw = this.pay_info.bank_info || this.pay_info.offline_bank_info
      if (raw && typeof raw === 'object') return raw
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw)
        } catch (e) {}
      }
      return {}
    },
    bankFields () {
      var bank = this.bankInfo
      return [
        { label: 'Account Holder Name', value: bank.account_holder_name || this.pay_info.company_name },
        { label: 'Account Holder Address', value: bank.account_holder_address || this.pay_info.company_address_detail },
        { label: 'Bank Account', value: bank.bank_account || this.pay_info.bank_account_name },
        { label: 'Bank Name', value: bank.bank_name || this.pay_info.bank_name },
        { label: 'Bank Address', value: bank.bank_address || this.pay_info.bank_address },
        { label: 'SWIFT CODE', value: bank.swift_code || bank.bank_swift_code || this.pay_info.bank_swift_code },
        { label: 'BANK CODE', value: bank.bank_code || this.pay_info.bank_account_number },
        { label: 'Branch Code', value: bank.branch_code || this.pay_info.bank_branch_code }
      ]
    },
    containerRecommend () {
      return this.container_recommend || this.order_info.container_recommend || null
    },
    containerOptions () {
      var recommend = this.containerRecommend || {}
      var returned = recommend.containers || []
      if (returned.length > 1) return returned
      var returnedMap = {}
      returned.forEach(item => {
        returnedMap[item.type] = item
      })
      return this.defaultContainerTypes.map(item => {
        return Object.assign({}, item, this.buildContainerOption(item), returnedMap[item.type] || {})
      })
    },
    activeContainer () {
      if (!this.containerOptions.length) return null
      var selected = this.containerOptions.find(item => item.type === this.activeContainerType)
      return selected || this.containerOptions[0]
    }
  },
  created: function () {
    this.getMemberPayInfo()
  },
  watch: {
    showNotice: function (val) {
      if (val && this.notice) {
        MessageBox.confirm('If you have completed the payment, please click the "Complete Payment" button, if you have not completed the payment, please click "Repay" to initiate the payment again', 'Confirm whether the payment is complete', { cancelButtonText: 'Pay again', confirmButtonText: 'Payment completed' }).then(action => {
          this.goNext()
        })
      }
    }
  },
  methods: {
    goBack () {
      if (this.notice) {
        this.$router.go(-2)
      } else {
        this.$router.go(-1)
      }
    },
    setClass (code) {
      let obj = {}
      obj['icon ' + code] = true
      return obj
    },
    goNext () {
      this.$router.push({ name: 'MemberOrderList' })
    },
    phaseLabel (phase, count) {
      if (phase === 1) return 'Deposit'
      if (phase === count) return 'Balance Payment'
      return 'Interim Payment'
    },
    phaseStatus (phase) {
      var state = this.currentPaymentState
      if (this.isCompleted || phase < this.currentPhase || (phase === this.currentPhase && state === 2)) return 'complete'
      if (phase > this.currentPhase) return 'upcoming'
      if (this.isPaymentPending) return 'pending'
      if (this.isPaymentRejected) return 'rejected'
      return 'current'
    },
    formatNumber (value) {
      var number = parseFloat(value)
      if (isNaN(number)) return value || 0
      return number.toFixed(6).replace(/\.?0+$/, '')
    },
    formatCurrency (value) {
      return '$' + this.formatNumber(value)
    },
    formatContainerType (type) {
      if (!type) return ''
      return String(type).replace(/^(\d+)([A-Z]+)$/, function (match, size, code) {
        return size + "' " + code
      })
    },
    selectContainer (type) {
      this.activeContainerType = type
    },
    syncInterimRatio () {
      if (!this.canSelectInterimRatio) {
        this.selectedInterimRatio = 0
        return
      }
      if (!this.selectedInterimRatio || this.selectedInterimRatio > this.maxInterimRatio) {
        this.selectedInterimRatio = this.maxInterimRatio
      }
    },
    selectInterimRatio (ratio) {
      if (ratio > this.maxInterimRatio) return
      this.selectedInterimRatio = ratio
    },
    confirmInterimRatio () {
      if (!this.selectedInterimRatio || this.selectedInterimRatio > this.maxInterimRatio) {
        Toast('Please select the interim payment ratio')
        return
      }
      this.interimSubmitting = true
      Indicator.open()
      selectInstallmentRatio(this.pay_sn, this.selectedInterimRatio).then(res => {
        Indicator.close()
        this.interimSubmitting = false
        Toast(res.message || 'Success')
        this.getMemberPayInfo()
      }).catch(error => {
        Indicator.close()
        this.interimSubmitting = false
        Toast(error.message || 'Update failed')
      })
    },
    buildContainerOption (container) {
      var totalVolume = parseFloat((this.containerRecommend || {}).total_volume || this.order_info.goods_volume_total) || 0
      var totalWeight = parseFloat((this.containerRecommend || {}).total_weight || this.order_info.goods_weight_total) || 0
      var neededByVolume = container.volume > 0 ? Math.ceil(totalVolume / container.volume) : 1
      var neededByWeight = container.max_weight > 0 ? Math.ceil(totalWeight / container.max_weight) : 1
      var needed = Math.max(1, neededByVolume, neededByWeight)
      return {
        needed_count: needed,
        volume_ratio: container.volume > 0 ? Math.min(100, Math.round(totalVolume / (container.volume * needed) * 1000) / 10) : 0,
        weight_ratio: container.max_weight > 0 ? Math.min(100, Math.round(totalWeight / (container.max_weight * needed) * 1000) / 10) : 0,
        can_fit_single: true
      }
    },
    containerRatio (container) {
      if (!container) return 0
      var ratio = parseFloat(container.volume_ratio)
      if (isNaN(ratio)) return 0
      return parseFloat(Math.max(0, Math.min(ratio, 999)).toFixed(2))
    },
    containerFillStyle (container) {
      var ratio = Math.min(this.containerRatio(container), 100)
      return {
        width: (ratio * 0.795) + '%'
      }
    },
    containerFillOutsideStyle (container) {
      var ratio = Math.min(this.containerRatio(container), 100)
      return {
        left: (18.15 + ratio * 0.795) + '%'
      }
    },
    containerUsageClass (container) {
      var ratio = this.containerRatio(container)
      if (ratio > 90) return 'usage-danger'
      if (ratio > 75) return 'usage-warning'
      return 'usage-normal'
    },
    getExportUrl (type) {
      var params = [
        'pay_sn=' + encodeURIComponent(this.pay_sn)
      ]
      if (this.activeContainer && this.activeContainer.type) {
        params.push('container_type=' + encodeURIComponent(this.activeContainer.type))
      }
      var action = type === 'excel' ? 'export_offline_excel' : 'export_offline_pdf'
      return env.API_HOST + '/memberbuy/' + action + '?' + params.join('&')
    },
    exportFile (type) {
      if (!this.token) {
        Toast('Please login first')
        return
      }
      var fileType = type === 'excel' ? 'xlsx' : 'pdf'
      Indicator.open()
      axios({
        method: 'GET',
        url: this.getExportUrl(type),
        responseType: 'blob',
        withCredentials: true,
        headers: {
          'X-DS-KEY': this.token
        }
      }).then(res => {
        var contentType = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || ''
        if (contentType.indexOf('application/json') !== -1 || contentType.indexOf('text/html') !== -1) {
          this.showExportError(res.data)
          Indicator.close()
          return
        }
        var disposition = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])
        var fileName = 'WireTransfer_' + this.pay_sn + '.' + fileType
        if (disposition) {
          var matched = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
          if (matched && matched[1]) fileName = decodeURIComponent(matched[1])
        }
        this.downloadBlob(fileName, res.data)
        Indicator.close()
      }).catch(error => {
        Indicator.close()
        Toast(error.message || 'Export failed')
      })
    },
    showExportError (blob) {
      var reader = new FileReader()
      reader.onload = function () {
        var message = 'Export failed'
        try {
          var json = JSON.parse(reader.result)
          message = json.message || message
        } catch (e) {
          if (String(reader.result || '').indexOf('login') !== -1) {
            message = 'Please login first'
          }
        }
        Toast(message)
      }
      reader.readAsText(blob)
    },
    downloadBlob (fileName, blob) {
      var aLink = document.createElement('a')
      var url = URL.createObjectURL(blob)
      aLink.href = url
      aLink.download = fileName
      document.body.appendChild(aLink)
      aLink.click()
      document.body.removeChild(aLink)
      URL.revokeObjectURL(url)
    },
    getImageUrl (item) {
      var image = item.image_url || item.goods_image_url || item.goods_image
      if (!image) return require('../../../assets/image/no_image.jpg')
      if (image.indexOf('data:') === 0 || image.indexOf('http') === 0 || image.indexOf('/') === 0) return image
      return 'data:image/png;base64,' + image
    },
    goGoods (goodsId) {
      this.$router.push({ name: 'HomeGoodsdetail', query: { goods_id: goodsId } })
    },
    getMemberPayInfo () {
      let _this = this
      this.loading = true
      this.loadError = ''
      getOrderpayOfflineInfo(
        this.pay_sn
      ).then((res) => {
        if (res.code === 10000) {
          var result = res.result || {}
          this.pay_info = result.pay_info || {}
          this.order_info = result.order_info || {}
          this.store_cart_list = result.store_cart_list || []
          this.container_recommend = result.container_recommend || null
          if (this.containerOptions.length) {
            var recommended = this.containerRecommend && this.containerRecommend.containers && this.containerRecommend.containers[0]
            this.activeContainerType = recommended && recommended.type ? recommended.type : this.containerOptions[0].type
          }
          this.syncInterimRatio()
        } else {
          this.loadError = res.message || 'Unable to load payment information.'
        }
        this.loading = false
        this.showNotice = 1
      }).catch(function (error) {
        if (error.code === 12001) { // 订单已经支付
          _this.goNext()
        } else {
          _this.loading = false
          _this.loadError = error.message || 'Unable to load payment information.'
          Toast(error.message)
        }
      })
    },
    submit () {
      if (!this.pay_info[this.currentImageField]) {
        Toast('Please upload your payment voucher')
        return
      }
      let _this = this
      updatePaymentInfo(this.pay_info).then(res => {
        // this.memberEdit(this.member_info)
        Toast(res.message)
        setTimeout(function () {
          _this.$router.push({ name: 'MemberOrderList' })
        }, 2000)
      }).catch(function (error) {
        Toast(error.message)
      })
    },
    // 页面元素转图片
    toImage () {
      var actions = this.$el.querySelectorAll('.no-capture')
      for (var i = 0; i < actions.length; i++) actions[i].style.display = 'none'

      // 第一个参数是需要生成截图的元素,第二个是自己需要配置的参数,宽高等
      html2canvas(this.$refs.imageTofile, {
        useCORS: true,
        // allowTaint: true,
        backgroundColor: null
      }).then((canvas) => {
        let url = canvas.toDataURL('image/png', 1)
        let f = 'Account_' + (new Date()).getTime()
        this.downloadFile(f, url)
        for (var j = 0; j < actions.length; j++) actions[j].style.display = ''
      })
    },
    downloadFile (fileName, content) {
      let aLink = document.createElement('a')
      let blob = this.base64ToBlob(content)
      let evt = document.createEvent('HTMLEvents')
      evt.initEvent('click', true, true)
      aLink.download = fileName
      aLink.href = URL.createObjectURL(blob)
      aLink.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
    },
    base64ToBlob (code) {
      let parts = code.split(';base64,')
      let contentType = parts[0].split(':')[1]
      let raw = window.atob(parts[1])
      let rawLength = raw.length
      let uInt8Array = new Uint8Array(rawLength)
      for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i)
      }
      return new Blob([uInt8Array], { type: contentType })
    },
    dropImage (type) {
      this.pay_info[type || this.currentImageField] = ''
      this.pay_info[type === 'balance_img' ? 'balance_img_url' : 'initial_img_url'] = ''
      this.$forceUpdate()
    },
    useCrop () {
      this.$refs.cropper.getCropBlob(res => {
        let files = new window.File([res], 'temp.png', { type: res.type })
        let formdata = new FormData()

        formdata.append('file', files)
        formdata.append('name', 'file')
        Indicator.open()
        uploadPaymentPic(formdata).then(res => {
          if (this.currentImageField === 'initial_img') {
            this.pay_info.initial_img = res.result.path
            this.pay_info.initial_img_url = res.result.url
          } else {
            this.pay_info.balance_img = res.result.path
            this.pay_info.balance_img_url = res.result.url
          }
          this.showCrop = false
          Indicator.close()
        }).catch(function (error) {
          Toast(error.message)
          Indicator.close()
        })
      })
    },
    uploadImage (event) {
      let that = this
      let file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) return
      this.cropType = this.currentImageField
      if (!/\.(jpg|png|gif|bmp|jpeg|webp)$/.test(file.name.toLowerCase())) {
        Toast('Please upload the file in picture format')
        event.target.value = ''
        return
      }
      this.showCrop = false
      this.cropperOption.img = ''
      let reader = new FileReader()
      reader.onload = (e) => {
        that.cropperOption.img = e.target.result
        let image = new Image()
        image.src = e.target.result
        image.onload = function () {
          that.cropperOption.autoCropWidth = window.screen.width
          that.cropperOption.autoCropHeight = this.height / this.width * window.screen.width
          that.cropperOption.maxImgSize = window.screen.height - 40
          that.showCrop = true
          event.target.value = ''
          that.$nextTick(() => {
            if (that.$refs.cropper && that.$refs.cropper.refresh) {
              that.$refs.cropper.refresh()
            }
          })
        }
      }
      reader.readAsDataURL(file)
    }

  }
}
</script>

<style scoped lang='scss'>
    .image_tofile {
      width: 100vw;
      height: 100vh;
      overflow: hidden;
      img{
        width: 100%;
        height: 100%;
      }
      .tip{
        position: absolute;
        width: 100vw;
        text-align: center;
        top: 20%;
      }
    }
    .btn {
      position: absolute;
      top: 30%;
    }
    #testImg {
      width: 100vw;
      height: 100vh;
      overflow: hidden;
    }
    .container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
        min-height: 100vh;
        background-color: $cardbgColor;
    }
    .offline_detail{
      width: 95%;
      margin: 10px auto 0;
      border: 1px solid #e6e7eb;
    }
    .offline_detail_t{
      padding: 0 10px;
      height: 42px;
      line-height: 40px;
      overflow: hidden;
      border-bottom: 1px solid #e6e7eb;
      background-color: #f9f9f9;
    }
    .offline_detail_t .title{
      display: inline;
      font-size: 14px;
      color: #333333;
    }
    .offline_detail_t .action{
      float: right;
    }
    .offline_detail_t .action #download{
      border-radius: 14px;
      padding: 0 15px;
      height: 24px;
      font-size: 14px;
      border-width: 1px;
      border-style: solid;
      background-color: #fff;
      border-color: #ff4040;
      color: #ff4040;
    }
    .offline_detail_b{
      padding: 5px;
      background-color: #fff;
    }
    .offline_detail_b .offline_tips{
      font-size: 12px;
      padding: 5px;
      border: 1px solid #7abee8;
    }
    .offline_detail_b .offline_info{
      margin-top: 5px;
      overflow: hidden;
    }
    .offline_detail_b .offline_info .offline_table{
      border-collapse: collapse;
      border-spacing: 0;
    }
    .offline_detail_b .offline_info .offline_table td{
      padding: 5px 5px;
      font-size: 12px;
      color: #666666;
      border: 1px solid #eeeeee;
    }
    .offline_detail_b .offline_info .offline_table td b{
      color: #333;
    }
    .offline_detail_b .offline_bot{
      padding: 0px 0px 5px;
      margin-top: 5px;
      background-color: #f7f8fa;
      overflow: hidden;
    }
    .offline_detail_b .offline_bot dl{
      overflow: hidden;
    }
    .offline_detail_b .offline_bot dl dt{
      position: relative;
      height: 18px;
      line-height: 18px;
      overflow: hidden;
      padding: 0 10px 0 7px;
      display: inline-block;
      background-color: #e3eaf7;
      color: #27A9E3;
      font-size: 12px;
    }
    .offline_detail_b .offline_bot dl dt:after {
      position: absolute;
      top: 0;
      right: 0;
      display: inline-block;
      width: 0;
      height: 0;
      border-color: #e3eaf7 #f7f8fa #e3eaf7 #e3eaf7;
      border-style: solid;
      border-width: 9px 8px 9px 0;
      content: "";
    }
    .offline_detail_b .offline_bot dl dd{
      line-height: 18px;
      font-size: 13px;
      padding: 0 10px;
      color: #666;
    }
    .offline_detail_b .offline_print_date{
      padding: 5px 0 0;
      color: #666;
      text-align: right;
      font-size: 12px;
      line-height: 16px;
    }
    .offline_upload_content{
      width: 95%;
      margin: 10px auto 0;
      border: 1px solid #e6e7eb;
    }
    .offline_upload_content .offline_print_body{}
    .offline_info_img{
      width: 8rem;
      height: 8rem;
      margin: 0 auto;
      padding: 20px 0;
      text-align: center;
      position: relative;
    }
    .offline_info_img::before{
      position: absolute;
      font-size: 1.5rem;
      line-height: 8rem;
      left: 50%;
      margin-left: -0.75rem;
      color: rgba(0, 0, 0, 0.5);
      z-index: 2;
    }
    .offline_info_img input{
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
    }
    .offline_info_img img{
      width: 100%;
      height: 100%;
    }

    .containers {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: stretch;
      background-color: #fff;
      border: 1px solid #eeeeee;
      margin-top: 5px;
    }
    .photo {
      width:4rem;
      height:4rem;
      margin:0.75rem 0.5rem 0.75rem 0.75rem;
      border: 1px solid #eeeeee;
      flex-basis:4rem;
      flex-shrink: 0;
    }
    .right-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: stretch;
      padding: 0 0.75rem 0 0;
      width: 100%;
      overflow: hidden;
    }
    .title {
      height: 2.4rem;
      line-height: 0.8rem;
      margin-top:0.75rem;
      color: #333;
      font-size:0.6rem;
      overflow: hidden;
      /*text-overflow: ellipsis;
      white-space: nowrap;*/
    }
    .property {
      font-size:0.65rem;
      color: #7c7f88;
      padding-top:0.5rem;
    }
    .count {
      margin-top:0.2rem;
      color: #7c7f88;
      font-size:0.65rem;
      margin-right:0.5rem;
    }
    .desc-wrapper {
      height:1rem;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding-top: 0.4rem;
      overflow: hidden;
    }
    .price {
      color: $primaryColor;
      font-size:0.8rem;
      margin-left: 0;
    }
    .count {
      color: #7c7f88;
      font-size:0.8rem;
      margin-left:0.5rem;
    }
    .offline-page {
      display: block;
      min-height: 100vh;
      background: #f5f6f8;
      color: #222;
    }
    .offline-content {
      width: 100%;
      box-sizing: border-box;
      padding: 0.6rem 0.5rem 3.7rem;
    }
    .page-card {
      width: 100%;
      margin-bottom: 0.5rem;
      padding: 0.75rem;
      background: #fff;
      border: 1px solid #e9eaed;
      border-radius: 0.2rem;
      box-sizing: border-box;
    }
    .hero-card {
      border-top: 0.15rem solid $primaryColor;
    }
    .hero-heading {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.5rem;
    }
    .section-kicker {
      margin-bottom: 0.25rem;
      color: #89909a;
      font-size: 0.5rem;
      font-weight: 600;
      letter-spacing: 0.05rem;
    }
    .hero-card h1 {
      margin: 0;
      color: #1f2933;
      font-size: 0.95rem;
      font-weight: 600;
      line-height: 1.3;
    }
    .order-reference {
      margin-top: 0.35rem;
      color: #727985;
      font-size: 0.58rem;
      line-height: 1.4;
      word-break: break-all;
    }
    .order-reference strong {
      color: #333;
      font-weight: 600;
    }
    .submit-button,
    .export-action,
    .type-tab,
    .interim-option,
    .interim-confirm {
      border: 0;
      border-radius: 0.2rem;
      cursor: pointer;
    }
    .hero-status {
      display: inline-block;
      margin-top: 0.7rem;
      padding: 0.25rem 0.5rem;
      border-radius: 0.15rem;
      font-size: 0.58rem;
      line-height: 1rem;
    }
    .status-action,
    .status-rejected {
      background: #fff1f1;
      color: #d9363e;
    }
    .status-pending {
      background: #fff7e6;
      color: #b76e00;
    }
    .status-approved {
      background: #edf7ff;
      color: #247ba0;
    }
    .status-complete {
      background: #edf9f0;
      color: #2d8a4a;
    }
    .notice-card {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      background: #fffaf0;
      border-color: #f2dfb0;
      color: #6f5a2b;
    }
    .notice-icon {
      flex: 0 0 auto;
      color: #d28b18;
      font-size: 0.9rem;
    }
    .notice-card strong {
      display: block;
      margin-bottom: 0.2rem;
      font-size: 0.68rem;
    }
    .notice-card p,
    .important-card p {
      margin: 0;
      font-size: 0.58rem;
      line-height: 1.55;
    }
    .section-title {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      min-height: 1rem;
      margin-bottom: 0.55rem;
      color: #1f2933;
      font-size: 0.72rem;
      font-weight: 600;
      line-height: 1.35;
    }
    .section-title span {
      color: #8a919a;
      font-size: 0.55rem;
      font-weight: 400;
    }
    .summary-row,
    .bank-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.75rem;
      min-height: 1.7rem;
      padding: 0.3rem 0;
      border-bottom: 1px solid #f1f2f4;
      box-sizing: border-box;
    }
    .summary-row:last-child,
    .bank-row:last-child {
      border-bottom: 0;
    }
    .summary-row span,
    .bank-row > span {
      flex: 0 0 42%;
      color: #707782;
      font-size: 0.6rem;
      line-height: 1.4;
    }
    .summary-row strong,
    .bank-value {
      flex: 1;
      min-width: 0;
      color: #333;
      font-size: 0.62rem;
      line-height: 1.4;
      text-align: right;
      overflow-wrap: anywhere;
    }
    .total-row {
      margin-top: 0.15rem;
      padding-top: 0.55rem;
    }
    .total-row b {
      color: $primaryColor;
      font-size: 0.82rem;
      font-weight: 600;
    }
    .reference-note {
      margin: 0.45rem 0 0;
      color: #a17843;
      font-size: 0.52rem;
      line-height: 1.45;
    }
    .progress-summary {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
      color: #6e7681;
      font-size: 0.58rem;
      line-height: 1.4;
    }
    .progress-summary strong {
      color: #333;
      font-size: 0.6rem;
      text-align: right;
    }
    .progress-item {
      position: relative;
      display: flex;
      min-height: 2.75rem;
    }
    .progress-marker {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 1.3rem;
      width: 1.3rem;
      height: 1.3rem;
      border: 1px solid #c9cdd3;
      border-radius: 50%;
      background: #fff;
      color: #89909a;
      font-size: 0.52rem;
      box-sizing: border-box;
    }
    .progress-line {
      position: absolute;
      top: 1.3rem;
      bottom: 0;
      left: 0.62rem;
      width: 1px;
      background: #dfe2e6;
    }
    .progress-item:last-child .progress-line {
      display: none;
    }
    .progress-body {
      flex: 1;
      min-width: 0;
      padding: 0 0 0.65rem 0.55rem;
    }
    .progress-label {
      color: #333;
      font-size: 0.62rem;
      line-height: 1.35;
    }
    .progress-label span {
      margin-left: 0.25rem;
      color: #89909a;
      font-size: 0.55rem;
    }
    .progress-amount {
      margin-top: 0.12rem;
      color: #555d68;
      font-size: 0.58rem;
    }
    .progress-time {
      margin-top: 0.1rem;
      color: #9ca3ad;
      font-size: 0.5rem;
    }
    .phase-complete .progress-marker {
      border-color: #43a866;
      background: #43a866;
      color: #fff;
    }
    .progress-check {
      display: block;
      width: 0.32rem;
      height: 0.18rem;
      margin-top: -0.06rem;
      border-left: 0.08rem solid #fff;
      border-bottom: 0.08rem solid #fff;
      transform: rotate(-45deg);
      box-sizing: border-box;
    }
    .phase-complete .progress-line {
      background: #43a866;
    }
    .phase-current .progress-marker,
    .phase-pending .progress-marker {
      border-color: $primaryColor;
      color: $primaryColor;
    }
    .phase-rejected .progress-marker {
      border-color: #d9363e;
      color: #d9363e;
    }
    .store-block + .store-block {
      margin-top: 0.6rem;
      padding-top: 0.6rem;
      border-top: 1px solid #eee;
    }
    .store-heading {
      margin-bottom: 0.35rem;
      color: #555d68;
      font-size: 0.6rem;
      font-weight: 600;
    }
    .store-heading .iconfont {
      margin-right: 0.3rem;
      color: $primaryColor;
      font-size: 0.7rem;
    }
    .product-item {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      min-width: 0;
      padding: 0.45rem 0;
      border-bottom: 1px solid #f1f2f4;
    }
    .product-item:last-child {
      border-bottom: 0;
    }
    .product-image {
      flex: 0 0 3.2rem;
      width: 3.2rem;
      height: 3.2rem;
      border: 1px solid #eee;
      object-fit: contain;
      box-sizing: border-box;
    }
    .product-main {
      flex: 1;
      min-width: 0;
    }
    .product-name {
      display: -webkit-box;
      overflow: hidden;
      color: #333;
      font-size: 0.62rem;
      line-height: 1.4;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    .product-meta {
      margin-top: 0.2rem;
      color: #858c95;
      font-size: 0.55rem;
      line-height: 1.35;
    }
    .product-subtotal {
      flex: 0 0 auto;
      max-width: 5rem;
      color: $primaryColor;
      font-size: 0.58rem;
      line-height: 1.35;
      text-align: right;
      overflow-wrap: anywhere;
    }
    .container-summary {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 0.55rem;
    }
    .container-summary > div {
      flex: 1;
      padding: 0.45rem;
      background: #f7f8fa;
      text-align: center;
    }
    .container-summary span,
    .container-summary strong {
      display: block;
    }
    .container-summary span {
      color: #858c95;
      font-size: 0.52rem;
    }
    .container-summary strong {
      margin-top: 0.15rem;
      color: #333;
      font-size: 0.7rem;
    }
    .container-type-tabs {
      display: flex;
      gap: 0.35rem;
      margin-bottom: 0.6rem;
      padding-bottom: 0.05rem;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
    .type-tab {
      flex: 0 0 auto;
      min-width: 3rem;
      height: 1.5rem;
      padding: 0 0.45rem;
      border: 1px solid #d8dde3;
      background: #f7f8fa;
      color: #515966;
      font-size: 0.58rem;
      line-height: 1.45rem;
    }
    .type-tab.active {
      border-color: #27a9e3;
      background: #eaf7fc;
      color: #1584b4;
      font-weight: 600;
    }
    .container-item {
      padding: 0.6rem;
      border: 1px solid #e4e7eb;
      border-radius: 0.2rem;
      background: #fafbfc;
    }
    .container-heading {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.5rem;
      margin-bottom: 0.55rem;
      color: #333;
      font-size: 0.62rem;
    }
    .container-heading span {
      margin-left: 0.2rem;
      color: $primaryColor;
    }
    .container-heading em {
      flex: 1;
      min-width: 0;
      color: #858c95;
      font-size: 0.55rem;
      font-style: normal;
      line-height: 1.35;
      text-align: right;
    }
    .container-visual {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
    }
    .container-svg-wrapper {
      position: relative;
      width: 100%;
      max-width: 16rem;
    }
    .container-svg-wrapper svg {
      display: block;
      width: 100%;
      height: auto;
    }
    .container-fill-bar {
      position: absolute;
      top: 8.75%;
      left: 18.15%;
      max-width: 79.5%;
      height: 63.75%;
      border-radius: 0.08rem;
      background: linear-gradient(90deg, #27a9e3, #5bc0de);
      opacity: 0.78;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-right: 0.25rem;
      box-sizing: border-box;
      transition: width 0.2s ease;
    }
    .container-fill-bar.high {
      background: linear-gradient(90deg, #ff7737, #ff4040);
    }
    .container-fill-text,
    .container-fill-text-outside {
      color: #fff;
      font-size: 0.56rem;
      font-weight: 600;
      line-height: 1;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
      white-space: nowrap;
    }
    .container-fill-text-outside {
      position: absolute;
      top: 35%;
      transform: translateX(0.25rem);
    }
    .container-label {
      margin-top: 0.25rem;
      color: #858c95;
      font-size: 0.52rem;
      text-align: center;
    }
    .container-info {
      display: block;
      margin-top: 0.55rem;
      padding: 0.15rem 0.55rem;
      background: #fff;
    }
    .container-info-row {
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      min-height: 1.45rem;
      padding: 0.25rem 0;
      border-bottom: 1px solid #f1f2f4;
    }
    .container-info-row:last-child {
      border-bottom: 0;
    }
    .container-info-row span {
      flex: 0 0 auto;
      color: #858c95;
      font-size: 0.55rem;
      line-height: 1.35;
      white-space: nowrap;
    }
    .container-info-row strong {
      flex: 1;
      min-width: 0;
      color: #333;
      font-size: 0.58rem;
      font-weight: 600;
      line-height: 1.35;
      text-align: right;
      overflow-wrap: anywhere;
    }
    .usage-normal {
      color: #27a9e3 !important;
    }
    .usage-warning {
      color: #ff7737 !important;
    }
    .usage-danger {
      color: $primaryColor !important;
    }
    .bank-row {
      gap: 0.5rem;
    }
    .bank-value {
      display: flex;
      align-items: flex-start;
      justify-content: flex-end;
      gap: 0.3rem;
    }
    .bank-value strong {
      min-width: 0;
      font-weight: 400;
      overflow-wrap: anywhere;
    }
    .important-card {
      color: #606873;
    }
    .interim-card {
      border-color: #dfe8f8;
    }
    .interim-desc {
      margin: 0 0 0.65rem;
      color: #606873;
      font-size: 0.58rem;
      line-height: 1.5;
    }
    .interim-options {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 0.45rem;
      margin-bottom: 0.7rem;
    }
    .interim-option {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
      height: 1.8rem;
      border: 1px solid #d8dde3;
      background: #fff;
      color: #333;
      font-size: 0.6rem;
      line-height: 1.8rem;
      box-sizing: border-box;
    }
    .interim-option span {
      width: 0.48rem;
      height: 0.48rem;
      border: 1px solid #9aa3ad;
      border-radius: 50%;
      box-sizing: border-box;
    }
    .interim-option.active {
      border-color: $primaryColor;
      background: #fff7f7;
      color: $primaryColor;
      font-weight: 600;
    }
    .interim-option.active span {
      border-color: $primaryColor;
      box-shadow: inset 0 0 0 0.12rem #fff;
      background: $primaryColor;
    }
    .interim-option.disabled {
      border-color: #edf0f3;
      background: #fafafa;
      color: #b9bec5;
      cursor: not-allowed;
    }
    .interim-option.disabled span {
      border-color: #d5d9de;
    }
    .interim-amount {
      margin-bottom: 0.75rem;
      color: $primaryColor;
      font-size: 0.68rem;
      font-weight: 600;
      line-height: 1.35;
    }
    .interim-confirm {
      min-width: 5.2rem;
      height: 1.8rem;
      padding: 0 0.85rem;
      background: $primaryColor;
      color: #fff;
      font-size: 0.62rem;
      font-weight: 600;
      line-height: 1.8rem;
      text-align: center;
    }
    .interim-confirm:disabled {
      background: #c8ccd2;
      cursor: not-allowed;
    }
    .important-card p + p {
      margin-top: 0.25rem;
    }
    .print-date {
      margin-top: 0.5rem;
      color: #9ca3ad;
      font-size: 0.52rem;
      text-align: right;
    }
    .current-payment {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.6rem;
      background: #fff7f7;
      color: #606873;
      font-size: 0.6rem;
    }
    .current-payment strong {
      color: $primaryColor;
      font-size: 0.78rem;
    }
    .upload-pending {
      margin-top: 0.5rem;
      padding: 0.45rem 0.5rem;
      background: #fff7e6;
      color: #9a681a;
      font-size: 0.56rem;
      line-height: 1.4;
    }
    .upload-rejected {
      background: #fff1f1;
      color: #c3323a;
    }
    .voucher-link {
      display: inline-block;
      margin-top: 0.5rem;
      color: #277da3;
      font-size: 0.58rem;
    }
    .voucher-preview {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 8rem;
      height: 8rem;
      margin: 0.75rem auto 0;
      padding: 0.25rem;
      border: 1px solid #e2e6ea;
      border-radius: 0.2rem;
      background: #fafbfc;
      box-sizing: border-box;
    }
    .voucher-preview img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
    .upload-card .offline_info_img {
      width: 8rem;
      height: 8rem;
      margin: 0.75rem auto;
      padding: 0;
      border: 1px dashed #cfd4da;
      border-radius: 0.2rem;
      background: #fafbfc;
      box-sizing: border-box;
    }
    .upload-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      height: 100%;
      gap: 0.35rem;
      color: #9aa1aa;
      font-size: 0.55rem;
    }
    .upload-placeholder .iconfont {
      font-size: 1.2rem;
    }
    .upload-card .offline_info_img img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .submit-button {
      display: block;
      width: 100%;
      height: 2.1rem;
      background: $primaryColor;
      color: #fff;
      font-size: 0.68rem;
      line-height: 2.1rem;
    }
    .completed-card {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
      color: #2d8a4a;
      font-size: 0.68rem;
    }
    .completed-check {
      display: block;
      width: 0.55rem;
      height: 0.3rem;
      margin-top: -0.08rem;
      border-left: 0.1rem solid #2d8a4a;
      border-bottom: 0.1rem solid #2d8a4a;
      transform: rotate(-45deg);
      box-sizing: border-box;
    }
    .page-state {
      padding: 3rem 1rem;
      color: #858c95;
      font-size: 0.7rem;
      text-align: center;
    }
    .page-state-error {
      color: #c3323a;
    }
    .export-bar {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 20;
      display: flex;
      gap: 0.45rem;
      padding: 0.45rem 0.55rem calc(0.45rem + env(safe-area-inset-bottom));
      background: rgba(255, 255, 255, 0.96);
      border-top: 1px solid #e8eaee;
      box-shadow: 0 -0.15rem 0.6rem rgba(20, 29, 38, 0.08);
      box-sizing: border-box;
    }
    .export-action {
      flex: 1;
      height: 1.9rem;
      color: #fff;
      font-size: 0.66rem;
      font-weight: 600;
      line-height: 1.9rem;
      text-align: center;
    }
    .export-pdf {
      background: #e74c3c;
    }
    .export-excel {
      background: #27ae60;
    }
    @media only screen and (max-width: 350px) {
      .offline-content {
        padding-left: 0.35rem;
        padding-right: 0.35rem;
      }
      .page-card {
        padding-left: 0.6rem;
        padding-right: 0.6rem;
      }
      .summary-row span,
      .bank-row > span {
        flex-basis: 39%;
      }
      .product-subtotal {
        max-width: 4.2rem;
      }
    }
</style>
